import { useEffect, useMemo, useState } from "react";
import Countdown from "./Countdown";
import VoucherConditionModal from "components/PromoCodes/VoucherConditionModal";
import { fetchMyVourcherList, validateVoucher } from "../../api/Vourcher";
import { MyVourcherItem } from "../../api/VourcherType";
import { toast } from "react-toastify";

export interface Voucher {
    id: string;
    code?: string;
    title: string;
    desc: string;
    exp_date: string;
    disabled?: boolean;
}

interface Props {
    open: boolean;
    onClose: () => void;
    vouchers: Voucher[];
    validateItems?: { product_id: number; quantity: number }[];
    quoteStatusMap?: Record<string, {
        success: boolean;
        message?: string;
        template_name?: string;
        discount_applied?: number;
        applied_to?: 'order' | 'shipping';
        type?: 'fixed' | 'percent' | 'ship' | 'gift';
    }>;
    defaultSelected?: string[];
    onSubmit: (ids: string[]) => void;
    singleSelect?: boolean;
}

function getMyVoucherTitle(item: MyVourcherItem): string {
    const valueNum = Number(item.voucher.value || 0);
    if (item.voucher.type === "percent") return `GIẢM ${valueNum}%`;
    if (item.voucher.type === "ship") return "GIẢM PHÍ SHIP";
    if (item.voucher.type === "gift") return "QUÀ TẶNG";
    return `GIẢM ${new Intl.NumberFormat("vi-VN").format(valueNum)}đ`;
}

function getMyVoucherDesc(item: MyVourcherItem): string {
    const firstRule = item.voucher.rules?.find(rule => !!rule.rule_description);
    if (firstRule?.rule_description) return firstRule.rule_description;
    return item.voucher.name || item.voucher.code;
}

export default function PromoVoucherModal({
    open,
    onClose,
    vouchers,
    validateItems = [],
    quoteStatusMap = {},
    defaultSelected = [],
    onSubmit,
    singleSelect
}: Props) {

    const [search, setSearch] = useState("");
    const [voucherCode, setVoucherCode] = useState("");
    const [selected, setSelected] = useState<string[]>(defaultSelected);
    const [openVoucher, setOpenVoucher] = useState(false);
    const [activeVoucherId, setActiveVoucherId] = useState<string | null>(null);
    const [myVouchers, setMyVouchers] = useState<Voucher[]>([]);
    const [loadingMyVouchers, setLoadingMyVouchers] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // lock scroll body when modal open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            setSelected(defaultSelected);
        }
    }, [defaultSelected, open]);

    useEffect(() => {
        const loadMyVouchers = async () => {
            if (!open) return;

            setLoadingMyVouchers(true);
            const res = await fetchMyVourcherList();
            if (!res.status || !res.data?.items) {
                setMyVouchers([]);
                setLoadingMyVouchers(false);
                return;
            }

            const uniqueMap = new Map<string, Voucher>();
            res.data.items
                .filter((item) => item.status === "AVAILABLE")
                .forEach((item) => {
                    const key = item.voucher.code || String(item.voucher.id);
                    if (uniqueMap.has(key)) return;

                    uniqueMap.set(key, {
                        id: String(item.voucher.id),
                        code: item.voucher.code,
                        title: getMyVoucherTitle(item),
                        desc: getMyVoucherDesc(item),
                        exp_date: item.expires_at || item.voucher.end_date || "",
                        disabled: false,
                    });
                });

            setMyVouchers(Array.from(uniqueMap.values()));
            setLoadingMyVouchers(false);
        };

        loadMyVouchers();
    }, [open]);

    // search filter
    const filtered = useMemo(() => {
        return vouchers.filter(v =>
            v.title.toLowerCase().includes(search.toLowerCase()) ||
            v.desc.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, vouchers]);

    const filteredMyVouchers = useMemo(() => {
        return myVouchers.filter(v =>
            v.title.toLowerCase().includes(search.toLowerCase()) ||
            v.desc.toLowerCase().includes(search.toLowerCase()) ||
            (v.code || "").toLowerCase().includes(search.toLowerCase())
        );
    }, [myVouchers, search]);

    const filteredSellerVouchers = useMemo(() => {
        if (filteredMyVouchers.length === 0) return filtered;

        const myCodes = new Set(filteredMyVouchers.map(v => (v.code || "").toUpperCase()).filter(Boolean));
        return filtered.filter(v => {
            const code = (v.code || "").toUpperCase();
            if (!code) return true;
            return !myCodes.has(code);
        });
    }, [filtered, filteredMyVouchers]);

    // toggle item
    const isVoucherInvalidByQuote = (voucher: Voucher) => {
        if (!voucher.code) return false;
        const status = quoteStatusMap[voucher.code];
        return status ? !status.success : false;
    };

    const toggle = (id: string, disabled?: boolean) => {
        const voucher = [...vouchers, ...myVouchers].find(item => item.id === id);
        if (disabled || (voucher && isVoucherInvalidByQuote(voucher))) return;

        // Chỉ cho phép chọn 1 mã giảm giá
        setSelected(prev => prev.includes(id) ? [] : [id]);
    };

    // submit
    const handleSubmit = async () => {
        if (selected.length === 0) {
            onSubmit([]);
            onClose();
            return;
        }

        // Tìm voucher đã chọn
        const selectedVoucher = [...myVouchers, ...vouchers].find(v => v.id === selected[0]);
        if (!selectedVoucher || !selectedVoucher.code) {
            toast.error("Không tìm thấy thông tin mã giảm giá.", { position: "bottom-right" });
            setSelected([]);
            return;
        }

        if (validateItems.length === 0) {
            toast.error("Không có sản phẩm nào trong giỏ hàng để áp dụng mã giảm giá.", { position: "bottom-right" });
            setSelected([]);
            return;
        }

        setSubmitting(true);
        try {
            const validateRes = await validateVoucher(selectedVoucher.code, validateItems);
            if (!validateRes.success) {
                const errorMessage = validateRes.errors && validateRes.errors.length > 0
                    ? validateRes.errors[0]
                    : validateRes.message || "Mã giảm giá không hợp lệ.";
                toast.error(errorMessage, { position: "bottom-right" });
                setSelected([]);
                return;
            }

            onSubmit(selected);
            onClose();
        } catch {
            toast.error("Kiểm tra mã giảm giá thất bại. Vui lòng thử lại.", { position: "bottom-right" });
            setSelected([]);
        } finally {
            setSubmitting(false);
        }
    };

    const handleApplyVoucherCode = async () => {
        const inputCode = voucherCode.trim().toUpperCase();
        if (!inputCode) {
            toast.error("Vui lòng nhập mã voucher.", {
                position: "bottom-right",
            });
            return;
        }

        if (validateItems.length === 0) {
            toast.error("Không có sản phẩm nào trong giỏ hàng để áp dụng mã giảm giá.", {
                position: "bottom-right",
            });
            return;
        }

        const validateRes = await validateVoucher(inputCode, validateItems);
        if (!validateRes.success) {
            const errorMessage = validateRes.errors && validateRes.errors.length > 0
                ? validateRes.errors[0]
                : validateRes.message || "Mã giảm giá không hợp lệ.";
            toast.error(errorMessage, {
                position: "bottom-right",
            });
            return;
        }

        const matchedVoucher = vouchers.find(v => (v.code || v.title || '').trim().toUpperCase() === inputCode);
        if (!matchedVoucher) {
            toast.error("Mã hợp lệ nhưng không có trong danh sách hiện tại.", {
                position: "bottom-right",
            });
            return;
        }

        if (isVoucherInvalidByQuote(matchedVoucher)) {
            const invalidMessage = matchedVoucher.code ? quoteStatusMap[matchedVoucher.code]?.message : "";
            toast.error(invalidMessage || "Mã giảm giá này không khả dụng cho đơn hiện tại.", {
                position: "bottom-right",
            });
            return;
        }

        if (matchedVoucher.disabled) {
            toast.error("Đơn hàng chưa đủ điều kiện áp dụng mã này.", {
                position: "bottom-right",
            });
            return;
        }

        if (singleSelect) {
            setSelected([matchedVoucher.id]);
        } else {
            setSelected(prev => prev.includes(matchedVoucher.id) ? prev : [...prev, matchedVoucher.id]);
        }

        setVoucherCode("");
        toast.success(validateRes.message || "Đã thêm mã giảm giá.", {
            position: "bottom-right",
        });
    };

    if (!open) return null;

    return (
        <div
            role="presentation"
            id="modalPromoList"
            className="MuiModal-root styles_modalContainer__Sdvdc css-8ndowl"
        >
            {/* backdrop */}
            <div
                className="MuiBackdrop-root"
                style={{ opacity: 1 }}
                onClick={onClose}
            />

            {/* modal main */}
            <div className="MuiGrid-root styles_confirm_modal_wrap__q6yk7">

                {/* HEADER */}
                <div className="MuiGrid-root styles_modal_title__Tpult MuiGrid-container MuiGrid-align-items-xs-center MuiGrid-justify-content-xs-space-between">
                    <p className="MuiTypography-root styles_modalVoucherList_title__m5Bv3 MuiTypography-body1">
                        Danh sách mã giảm giá
                    </p>

                    <svg
                        className="MuiSvgIcon-root styles_close__JfKfm"
                        viewBox="0 0 24 24"
                        onClick={onClose}
                    >
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </div>

                <div className="MuiBox-root css-n6fgso">
                    {/* NOTE */}
                    <div className="styles_notifySelectVoucher__DIGpC">
                        Mỗi lần thanh toán chỉ được chọn 1 mã giảm giá
                    </div>

                    <div className="MuiBox-root css-8atqhb" style={{ marginBottom: 8 }}>
                        <div className="MuiFormControl-root MuiTextField-root styles_inputSearchVoucher__8IG7n">
                            <div className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-formControl MuiInputBase-adornedEnd MuiOutlinedInput-adornedEnd">
                                <input
                                    aria-invalid="false"
                                    placeholder="Nhập mã voucher"
                                    type="text"
                                    className="MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputAdornedEnd MuiOutlinedInput-inputAdornedEnd"
                                    value={voucherCode}
                                    onChange={e => setVoucherCode(e.target.value)}
                                />
                                <div className="MuiInputAdornment-root MuiInputAdornment-positionEnd">
                                    <button className="MuiButtonBase-root MuiButton-root MuiButton-text styles_btnSearchVoucher__CTqb3" type="button" onClick={handleApplyVoucherCode}>
                                        <span className="MuiButton-label">Áp dụng</span>
                                        <span className="MuiTouchRipple-root"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SEARCH */}
                    <div className="MuiBox-root css-8atqhb">
                        <div className="MuiFormControl-root MuiTextField-root styles_inputSearchVoucher__8IG7n">
                            <div className="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-formControl MuiInputBase-adornedStart MuiOutlinedInput-adornedStart MuiInputBase-adornedEnd MuiOutlinedInput-adornedEnd">
                                <div className="MuiInputAdornment-root MuiInputAdornment-positionStart">
                                    <span style={{ boxSizing: 'border-box', display: 'inline-block', overflow: 'hidden', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: '0px', margin: '0px', padding: '0px', position: 'relative', maxWidth: '100%' }}>
                                        <span style={{ boxSizing: 'border-box', display: 'block', width: '17px', height: 'initial', background: 'none', opacity: 1, border: '0px', margin: '0px', padding: '0px', maxWidth: '100%' }}>
                                            <img alt="" aria-hidden="true" src="https://thuocsi.vn/images/promoCode/coupon_icon.svg" style={{ display: 'block', maxWidth: '100%', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: '0px', margin: '0px', padding: '0px' }} />
                                        </span>
                                        <img alt="icon phone" src="https://thuocsi.vn/images/promoCode/coupon_icon.svg" decoding="async" data-nimg="intrinsic" style={{ position: 'absolute', inset: '0px', boxSizing: 'border-box', padding: '0px', border: 'none', margin: 'auto', display: 'block', width: '0px', height: '0px', minWidth: '100%', maxWidth: '100%', minHeight: '100%', maxHeight: '100%' }} />
                                    </span>
                                </div>
                                <input aria-invalid="false" placeholder="Nhập mã cần tìm" type="text" className="MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputAdornedStart MuiOutlinedInput-inputAdornedStart MuiInputBase-inputAdornedEnd MuiOutlinedInput-inputAdornedEnd" value={search}
                                    onChange={e => setSearch(e.target.value)} />
                                <div className="MuiInputAdornment-root MuiInputAdornment-positionEnd">
                                    <button className="MuiButtonBase-root MuiButton-root MuiButton-text styles_btnSearchVoucher__CTqb3" type="button">
                                        <span className="MuiButton-label">Tìm kiếm</span>
                                        <span className="MuiTouchRipple-root"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* LIST */}
                <div className="styles_coupon_list_wrapper__bgnqH">
                    <div className="styles_sectionVoucher_sticky__GZNXe">
                        <p className="MuiTypography-root styles_sectionVoucher_title__vXIVr MuiTypography-body1">Mã giảm giá của tôi</p>
                    </div>

                    {loadingMyVouchers && (
                        <div style={{ padding: 12, color: "#6b7280" }}>Đang tải mã đã lưu...</div>
                    )}

                    {!loadingMyVouchers && filteredMyVouchers.length === 0 && (
                        <div style={{ padding: 12, color: "#888" }}>Chưa có mã giảm giá đã lưu</div>
                    )}

                    {!loadingMyVouchers && filteredMyVouchers.map(v => (
                        <div key={`my-${v.id}-${v.code || ""}`} className="styles_voucherList_container__mD4a1">
                            {(() => {
                                const breakdown = v.code ? quoteStatusMap[v.code] : undefined;
                                return (
                                    <div
                                        className="MuiGrid-root"
                                        style={{
                                            position: "relative",
                                            overflow: "hidden",
                                            border: isVoucherInvalidByQuote(v) ? "1px solid #d4323b" : undefined,
                                            borderRadius: isVoucherInvalidByQuote(v) ? 8 : undefined,
                                            background: isVoucherInvalidByQuote(v) ? "#fff5f5" : undefined,
                                        }}
                                    >

                                        <div className="styles_voucherLeftMissingPart__Dd9yV" style={{ backgroundColor: "#fff" }}></div>

                                        <div className="MuiGrid-root styles_voucherItem_wrapper_cart__ejF4e">

                                            <div className="MuiGrid-root styles_voucherContainer_upper__P4pdw MuiGrid-container">

                                                <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-10 MuiGrid-grid-md-10" style={{ paddingRight: 8 }}>
                                                    <p className="MuiTypography-root styles_voucherContent_title__W0Q3Z MuiTypography-body2 MuiTypography-noWrap">
                                                        {v.title}
                                                    </p>

                                                    <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 14 }}>
                                                        <div className="styles_voucherContent_desc__ANOqH styles_content_2Line__BrNP1">
                                                            {v.desc}
                                                        </div>
                                                    </p>
                                                    {breakdown?.success && (
                                                        <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 12, marginTop: 6 }}>
                                                            {`${breakdown.template_name || "Voucher hợp lệ"} • Giảm ${(breakdown.discount_applied || 0).toLocaleString("vi-VN")}đ • Áp dụng: ${breakdown.applied_to === "shipping" ? "Shipping" : "Order"} • Loại: ${breakdown.type || "-"}`}
                                                        </p>
                                                    )}
                                                    {isVoucherInvalidByQuote(v) && (
                                                        <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 12, color: "#d4323b", marginTop: 6 }}>
                                                            {v.code ? (quoteStatusMap[v.code]?.message || "Mã không khả dụng cho giỏ hàng này") : "Mã không khả dụng cho giỏ hàng này"}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-2 MuiGrid-grid-md-2 d-none" style={{ position: "relative", marginTop: 15 }}>
                                                    <img src="https://cdn-web-next.thuocsi.vn/images/promoCode/logo_med_x.svg" alt="logo internal seller" style={{ width: 68, height: 68 }} />
                                                </div>
                                            </div>

                                            <div className="styles_voucherDivider__7rclz" />

                                            <div className="MuiGrid-root styles_voucherContainer_downer__DSU6c">

                                                <div className="MuiGrid-root MuiGrid-container">
                                                    <div className="MuiGrid-root styles_expiredDateBarWrapper__ffAl6 MuiGrid-item MuiGrid-grid-sm-4 MuiGrid-grid-md-4">
                                                        <p
                                                            className="MuiTypography-root MuiTypography-body2"
                                                            style={{
                                                                lineHeight: "14px",
                                                                color: "rgb(0, 0, 0)",
                                                                fontFamily: "ggsm",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            <span style={{ color: "rgb(212, 50, 59)" }}>
                                                                Còn <div style={{ display: "inline-block" }}><Countdown endDate={v.exp_date} /></div>
                                                            </span>
                                                        </p>
                                                    </div>

                                                    <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-sm-8 MuiGrid-grid-md-8">

                                                        <div className="MuiGrid-root" style={{ display: "flex", justifyContent: "space-between", padding: "0 20px 0 12px" }}>
                                                            <button
                                                                className="MuiButtonBase-root MuiButton-root MuiButton-text"
                                                                tabIndex={0}
                                                                type="button"
                                                                style={{
                                                                    color: "rgb(14, 25, 131)",
                                                                    padding: "6px 8px",
                                                                    textTransform: "unset",
                                                                    zIndex: 10,
                                                                }}
                                                                onClick={() => {
                                                                    setActiveVoucherId(v.id);
                                                                    setOpenVoucher(true);
                                                                }}
                                                            >
                                                                <span className="MuiButton-label">
                                                                    <svg
                                                                        className="MuiSvgIcon-root"
                                                                        focusable="false"
                                                                        viewBox="0 0 24 24"
                                                                        aria-hidden="true"
                                                                        style={{ width: 20, height: 20 }}
                                                                    >
                                                                        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                                                    </svg>

                                                                    <p
                                                                        className="MuiTypography-root MuiTypography-body2"
                                                                        style={{
                                                                            paddingLeft: 8,
                                                                            fontSize: 12,
                                                                            lineHeight: "18px",
                                                                        }}
                                                                    >
                                                                        Điều kiện
                                                                    </p>
                                                                </span>

                                                                <span className="MuiTouchRipple-root" />
                                                            </button>

                                                            <button className="MuiButtonBase-root MuiButton-root MuiButton-text styles_cartVoucherBtn__IanzC" tabIndex={0} type="button" onClick={() => toggle(v.id, v.disabled)}>
                                                                <span className="MuiButton-label">
                                                                    <span className="MuiButtonBase-root MuiIconButton-root jss231 MuiCheckbox-root MuiCheckbox-colorSecondary jss232 Mui-checked MuiIconButton-colorSecondary" aria-disabled={v.disabled || isVoucherInvalidByQuote(v)} style={{ padding: "0px", opacity: (v.disabled || isVoucherInvalidByQuote(v)) ? 0.6 : 1 }}>
                                                                        <span className="MuiIconButton-label">
                                                                            <input className="jss234" type="checkbox" data-indeterminate="false" aria-label="controlled" value="" checked />
                                                                            <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: "0px", margin: "0px", padding: "0px", position: "relative", maxWidth: "100%" }}>
                                                                                {selected.includes(v.id) ? (
                                                                                    <img
                                                                                        alt="checked"
                                                                                        src="https://cdn-web-next.thuocsi.vn/images/cart/checked_icon.svg"
                                                                                        style={{ width: 28, height: 28 }}
                                                                                    />
                                                                                ) : (
                                                                                    <img
                                                                                        alt="unchecked"
                                                                                        src="https://cdn-web-next.thuocsi.vn/images/cart/checkbox_icon.svg"
                                                                                        style={{ width: 28, height: 28 }}
                                                                                    />
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                        <span className="MuiTouchRipple-root"></span>
                                                                    </span>
                                                                </span>
                                                                <span className="MuiTouchRipple-root"></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="styles_voucherRightMissingPart__ghZZb" style={{ backgroundColor: "#fff" }}></div>
                                    </div>
                                );
                            })()}
                        </div>
                    ))}

                    <div className="styles_sectionVoucher_sticky__GZNXe">
                        <p className="MuiTypography-root styles_sectionVoucher_title__vXIVr MuiTypography-body1">Mã giảm giá từ nhà bán hàng</p>
                    </div>
                    {filteredSellerVouchers.map(v => (
                        <div key={v.id} className="styles_voucherList_container__mD4a1">
                            {(() => {
                                const breakdown = v.code ? quoteStatusMap[v.code] : undefined;
                                return (
                                    <div
                                        className="MuiGrid-root"
                                        style={{
                                            position: "relative",
                                            overflow: "hidden",
                                            border: isVoucherInvalidByQuote(v) ? "1px solid #d4323b" : undefined,
                                            borderRadius: isVoucherInvalidByQuote(v) ? 8 : undefined,
                                            background: isVoucherInvalidByQuote(v) ? "#fff5f5" : undefined,
                                        }}
                                    >

                                        <div className="styles_voucherLeftMissingPart__Dd9yV" style={{ backgroundColor: "#fff" }}></div>

                                        <div className="MuiGrid-root styles_voucherItem_wrapper_cart__ejF4e">

                                            <div className="MuiGrid-root styles_voucherContainer_upper__P4pdw MuiGrid-container">

                                                <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-10 MuiGrid-grid-md-10" style={{ paddingRight: 8 }}>
                                                    <p className="MuiTypography-root styles_voucherContent_title__W0Q3Z MuiTypography-body2 MuiTypography-noWrap">
                                                        {v.title}
                                                    </p>

                                                    <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 14 }}>
                                                        <div className="styles_voucherContent_desc__ANOqH styles_content_2Line__BrNP1">
                                                            {v.desc}
                                                        </div>
                                                    </p>
                                                    {breakdown?.success && (
                                                        <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 12, marginTop: 6 }}>
                                                            {`${breakdown.template_name || 'Voucher hợp lệ'} • Giảm ${(breakdown.discount_applied || 0).toLocaleString('vi-VN')}đ • Áp dụng: ${breakdown.applied_to === 'shipping' ? 'Shipping' : 'Order'} • Loại: ${breakdown.type || '-'}`}
                                                        </p>
                                                    )}
                                                    {isVoucherInvalidByQuote(v) && (
                                                        <p className="MuiTypography-root MuiTypography-body2" style={{ fontSize: 12, color: "#d4323b", marginTop: 6 }}>
                                                            {v.code ? (quoteStatusMap[v.code]?.message || "Mã không khả dụng cho giỏ hàng này") : "Mã không khả dụng cho giỏ hàng này"}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-2 MuiGrid-grid-md-2" style={{ position: "relative", marginTop: 15 }}>
                                                    <div style={{
                                                        width: 68,
                                                        height: 68
                                                    }}>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="styles_voucherDivider__7rclz" />

                                            <div className="MuiGrid-root styles_voucherContainer_downer__DSU6c">

                                                <div className="MuiGrid-root MuiGrid-container">
                                                    <div className="MuiGrid-root styles_expiredDateBarWrapper__ffAl6 MuiGrid-item MuiGrid-grid-sm-4 MuiGrid-grid-md-4">
                                                        <p
                                                            className="MuiTypography-root MuiTypography-body2"
                                                            style={{
                                                                lineHeight: "14px",
                                                                color: "rgb(0, 0, 0)",
                                                                fontFamily: "ggsm",
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            <span style={{ color: "rgb(212, 50, 59)" }}>
                                                                Còn <div style={{ display: "inline-block" }}><Countdown endDate={v.exp_date} /></div>
                                                            </span>
                                                        </p>

                                                        <div
                                                            className="MuiGrid-root"
                                                            style={{
                                                                width: "100%",
                                                                marginRight: "1px",
                                                                position: "relative",
                                                            }}
                                                        >
                                                            <div
                                                                className="MuiLinearProgress-root MuiLinearProgress-colorPrimary css-150lrg3 MuiLinearProgress-determinate"
                                                                role="progressbar"
                                                                aria-valuenow={25}
                                                                aria-valuemin={0}
                                                                aria-valuemax={100}
                                                            >
                                                                <div
                                                                    className="MuiLinearProgress-bar MuiLinearProgress-barColorPrimary MuiLinearProgress-bar1Determinate"
                                                                    style={{ transform: "translateX(-75%)" }}
                                                                />
                                                            </div>

                                                            <span
                                                                style={{
                                                                    position: "absolute",
                                                                    top: "0px",
                                                                    left: "50%",
                                                                    transform: "translate(-50%, 10%)",
                                                                    fontSize: "10px",
                                                                    fontFamily: "ggsm",
                                                                    lineHeight: "12px",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                Đã dùng: 25%
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-sm-8 MuiGrid-grid-md-8">

                                                        <div className="MuiGrid-root" style={{ display: "flex", justifyContent: "space-between", padding: "0 20px 0 12px" }}>
                                                            <button
                                                                className="MuiButtonBase-root MuiButton-root MuiButton-text"
                                                                tabIndex={0}
                                                                type="button"
                                                                style={{
                                                                    color: "rgb(14, 25, 131)",
                                                                    padding: "6px 8px",
                                                                    textTransform: "unset",
                                                                    zIndex: 10,
                                                                }}
                                                                onClick={() => {
                                                                    setActiveVoucherId(v.id);
                                                                    setOpenVoucher(true);
                                                                }}
                                                            >
                                                                <span className="MuiButton-label">
                                                                    <svg
                                                                        className="MuiSvgIcon-root"
                                                                        focusable="false"
                                                                        viewBox="0 0 24 24"
                                                                        aria-hidden="true"
                                                                        style={{ width: 20, height: 20 }}
                                                                    >
                                                                        <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                                                                    </svg>

                                                                    <p
                                                                        className="MuiTypography-root MuiTypography-body2"
                                                                        style={{
                                                                            paddingLeft: 8,
                                                                            fontSize: 12,
                                                                            lineHeight: "18px",
                                                                        }}
                                                                    >
                                                                        Điều kiện
                                                                    </p>
                                                                </span>

                                                                <span className="MuiTouchRipple-root" />
                                                            </button>

                                                            <button className="MuiButtonBase-root MuiButton-root MuiButton-text styles_cartVoucherBtn__IanzC" tabIndex={0} type="button" onClick={() => toggle(v.id, v.disabled)}>
                                                                <span className="MuiButton-label">
                                                                    <span className="MuiButtonBase-root MuiIconButton-root jss231 MuiCheckbox-root MuiCheckbox-colorSecondary jss232 Mui-checked MuiIconButton-colorSecondary" aria-disabled={v.disabled || isVoucherInvalidByQuote(v)} style={{ padding: '0px', opacity: (v.disabled || isVoucherInvalidByQuote(v)) ? 0.6 : 1 }}>
                                                                        <span className="MuiIconButton-label">
                                                                            <input className="jss234" type="checkbox" data-indeterminate="false" aria-label="controlled" value="" checked />
                                                                            <span style={{ boxSizing: 'border-box', display: 'inline-block', overflow: 'hidden', width: 'initial', height: 'initial', background: 'none', opacity: 1, border: '0px', margin: '0px', padding: '0px', position: 'relative', maxWidth: '100%' }}>
                                                                                {selected.includes(v.id) ? (
                                                                                    <img
                                                                                        alt="checked"
                                                                                        src="https://cdn-web-next.thuocsi.vn/images/cart/checked_icon.svg"
                                                                                        style={{ width: 28, height: 28 }}
                                                                                    />
                                                                                ) : (
                                                                                    <img
                                                                                        alt="unchecked"
                                                                                        src="https://cdn-web-next.thuocsi.vn/images/cart/checkbox_icon.svg"
                                                                                        style={{ width: 28, height: 28 }}
                                                                                    />
                                                                                )}
                                                                            </span>
                                                                        </span>
                                                                        <span className="MuiTouchRipple-root"></span>
                                                                    </span>
                                                                </span>
                                                                <span className="MuiTouchRipple-root"></span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="styles_voucherRightMissingPart__ghZZb" style={{ backgroundColor: "#fff" }}></div>
                                    </div>
                                );
                            })()}
                        </div>
                    ))}

                    {filtered.length === 0 && (
                        <div style={{ padding: 16, color: "#888" }}>
                            Không tìm thấy mã phù hợp
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="styles_modal_footer__MPe6I MuiBox-root css-0">
                    <p className="MuiTypography-root MuiTypography-body2">{selected.length} Mã giảm giá đã được chọn</p>
                    <button className="MuiButtonBase-root MuiButton-root MuiButton-text styles_modalButtonClose__eQOiz" onClick={handleSubmit} type="button" disabled={submitting}>
                        <span className="MuiButton-label">{submitting ? "Đang kiểm tra..." : "Đồng ý"}</span><span className="MuiTouchRipple-root"></span>
                    </button>
                </div>
            </div>
            <VoucherConditionModal
                open={openVoucher}
                voucherId={activeVoucherId}
                onClose={() => {
                    setOpenVoucher(false);
                    setActiveVoucherId(null);
                }}
            />
        </div>
    );
}
